import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, RefreshCw, Search, Terminal } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { AnsiHtml } from 'fancy-ansi/react';
import Select from 'react-select';
import { getDeviceLogs, getLogFile } from '../api/api';
import { Toast } from '../components/ui';
import styles from './DeviceLogs.module.scss';

function DeviceLogs() {
  const { serialNumber } = useParams();
  const [selectedLogFile, setSelectedLogFile] = useState('');
  const [logFiles, setLogFiles] = useState([]);
  const [logContent, setLogContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRegex, setIsRegex] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState('');
  const viewerRef = useRef(null);

  const fetchLogFiles = useCallback(async () => {
    try {
      setLoadingFiles(true);
      const response = await getDeviceLogs(serialNumber);
      const sortedFiles = [...(response.data || [])].sort().reverse();
      setLogFiles(sortedFiles);
      setSelectedLogFile(current =>
        sortedFiles.includes(current) ? current : sortedFiles[0] || ''
      );
      setError('');
    } catch {
      setError('Failed to load log file list.');
    } finally {
      setLoadingFiles(false);
    }
  }, [serialNumber]);

  const fetchLogContent = useCallback(async () => {
    if (!selectedLogFile) {
      setLogContent('');
      return;
    }
    try {
      setLoadingContent(true);
      const response = await getLogFile(serialNumber, selectedLogFile);
      setLogContent(response.data || '');
      setError('');
      requestAnimationFrame(() => {
        if (viewerRef.current) {
          viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
        }
      });
    } catch {
      setError('Failed to load selected log file.');
      setLogContent('');
    } finally {
      setLoadingContent(false);
    }
  }, [selectedLogFile, serialNumber]);

  useEffect(() => {
    fetchLogFiles();
  }, [fetchLogFiles]);

  useEffect(() => {
    fetchLogContent();
  }, [fetchLogContent]);

  const filteredResult = useMemo(() => {
    if (!searchQuery) {
      return { content: logContent, error: '', matches: 0 };
    }
    try {
      const matcher = isRegex ? new RegExp(searchQuery, 'i') : null;
      const matchingLines = logContent
        .split('\n')
        .filter(line =>
          matcher ? matcher.test(line) : line.toLowerCase().includes(searchQuery.toLowerCase())
        );
      return { content: matchingLines.join('\n'), error: '', matches: matchingLines.length };
    } catch {
      return { content: logContent, error: 'Invalid regular expression', matches: 0 };
    }
  }, [isRegex, logContent, searchQuery]);

  const refresh = async () => {
    await fetchLogFiles();
    await fetchLogContent();
  };

  const fileOptions = useMemo(
    () => logFiles.map(file => ({ value: file, label: file })),
    [logFiles]
  );
  const selectedFileOption = selectedLogFile
    ? { value: selectedLogFile, label: selectedLogFile }
    : null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Hub diagnostics</span>
          <h1>Logs</h1>
          <p>Select a captured log, then filter its lines without losing the original content.</p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={refresh}
          disabled={loadingFiles || loadingContent}
        >
          <RefreshCw size={16} className={loadingFiles || loadingContent ? styles.spinning : ''} />{' '}
          Refresh logs
        </button>
      </header>
      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}

      <div className={styles.logsWorkspace}>
        <div className={styles.fileBar}>
          <label className={styles.fileBarLabel} htmlFor="log-file-select">
            <FileText size={16} />
            <span>
              <strong>Log file</strong>
              <small>{logFiles.length} available</small>
            </span>
          </label>
          <Select
            inputId="log-file-select"
            classNamePrefix="logFileSelect"
            className={styles.fileSelect}
            options={fileOptions}
            value={selectedFileOption}
            onChange={option => option && setSelectedLogFile(option.value)}
            isSearchable
            isLoading={loadingFiles}
            isDisabled={!logFiles.length && !loadingFiles}
            placeholder={logFiles.length ? 'Search log files…' : 'No log files available'}
            noOptionsMessage={() => 'No matching log files'}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPlacement="auto"
          />
          <button
            type="button"
            className={styles.fileRefreshButton}
            onClick={fetchLogContent}
            disabled={!selectedLogFile || loadingContent}
            title="Reload the selected log file"
          >
            <RefreshCw size={15} className={loadingContent ? styles.spinning : ''} />
            <span>Refresh</span>
          </button>
        </div>

        <section className={styles.viewerPanel}>
          <div className={styles.viewerHeader}>
            <div>
              <Terminal size={16} />
              <span>
                <strong>{selectedLogFile || 'No log selected'}</strong>
                <small>
                  {logContent ? `${logContent.split('\n').length} lines` : 'No content'}
                </small>
              </span>
            </div>
            <label className={styles.searchField}>
              <Search size={15} />
              <span className={styles.srOnly}>Filter log lines</span>
              <input
                type="search"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Filter log lines"
              />
            </label>
            <label className={styles.regexToggle}>
              <input
                type="checkbox"
                checked={isRegex}
                onChange={event => setIsRegex(event.target.checked)}
              />
              <span>Regex</span>
            </label>
            {searchQuery && !filteredResult.error && <b>{filteredResult.matches} matches</b>}
          </div>
          {filteredResult.error && <div className={styles.searchError}>{filteredResult.error}</div>}
          <div className={styles.logViewerContainer} ref={viewerRef}>
            {loadingContent ? (
              <div className={styles.emptyState}>Loading log…</div>
            ) : filteredResult.content ? (
              <div className={styles.logViewer}>
                <AnsiHtml text={filteredResult.content} className="ansi-terminal" />
              </div>
            ) : (
              <div className={styles.emptyState}>
                {searchQuery ? 'No lines match this filter.' : 'No log content available.'}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default DeviceLogs;
